import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import Alert from 'react-s-alert';
import Raven from 'raven-js';

import Posts from 'api/resources/posts';
import { processErrorByAlert } from 'utils/getErrors';

import ReconnectingWebSocket from 'containers/ReconnectingWebSocket';

const EVENTS = ['mousemove', 'keyup', 'scroll'];
const TIMEOUT_DELAY = 60 * 60000; // час
const LOCK_TIMEOUT_DELAY = 40 * 60000; // 40 минут

export class PublicationLock extends React.Component {
  state = {
    shouldRedirectOnUnlock: true,
  }

  componentDidMount() {
    this.lockPublication();
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  componentWillUnmount() {
    if (this.props.shouldUnlockOnUnmount) {
      this.unlockPublication();
    }

    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    clearTimeout(this.timer);
    clearTimeout(this.lockTimer);

    EVENTS.forEach(event => window.removeEventListener(event, this.handleUserEvent));
  }

  handleAdminNotificationsMessage = async (response) => {
    if (!response || !response.body) return;

    if (response.type !== 'post') return;
    if (response.action !== 'unlock') return;
    if (response.body.id !== this.props.id) return;

    Alert.warning('Публикация разблокирована', { timeout: 'none' });

    // если это авторазблокировка на upload, выкидываем из публикации
    if (this.state.shouldRedirectOnUnlock) {
      this.props.history.push('/publications');
    }
  }

  handleBeforeUnload = (e) => {
    this.setState({ shouldRedirectOnUnlock: false }, () => this.unlockPublication({ byUnload: true }));

    const confirmationMessage = 'Разблокировка публикации перед закрытием вкладки';
    (e || window.event).returnValue = confirmationMessage;
    return confirmationMessage;
  }

  handleUserEvent = () => {
    clearTimeout(this.timer);
    this.timer = setTimeout(this.saveAndUnlock, TIMEOUT_DELAY);
  }

  saveAndUnlock = async () => {
    try {
      await Posts.patch({
        publicationId: this.props.id,
        body: { data: this.props.data() },
      });

      Alert.success('Изменения в публикации успешно сохранены', { timeout: 'none' });
    } catch (err) {
      processErrorByAlert(err);
      Alert.error('Изменения в публикации не удалось сохранить', { timeout: 'none' });
      Raven.captureException(err, { extra: this.props });
    }

    this.unlockPublication({ byTimeout: true });
  }

  lockPublication = async () => {
    try {
      await Posts.lock({ publicationId: this.props.id });

      EVENTS.forEach((event) => {
        window.removeEventListener(event, this.handleUserEvent);
        window.addEventListener(event, this.handleUserEvent);
      });

      if (!this.timer) {
        this.timer = setTimeout(this.saveAndUnlock, TIMEOUT_DELAY);
      }

      if (!this.lockTimer) {
        this.lockTimer = setTimeout(this.lockPublication, LOCK_TIMEOUT_DELAY);
      }
    } catch (err) {
      processErrorByAlert(err);
      Raven.captureException(err, { extra: this.props });
    }
  }

  async unlockPublication({ byTimeout, byUnload } = {}) {
    try {
      await Posts.unlock({ publicationId: this.props.id });

      if (byTimeout) {
        Alert.warning('Публикация разблокирована по таймауту', { timeout: 'none' });
      }

      if (byUnload) {
        Alert.warning(
          'Если у вас есть несохраненные изменения, скопируйте их и перезагрузите страницу.',
          { timeout: 'none' }
        );
      }
    } catch (err) {
      processErrorByAlert(err);
      Raven.captureException(err, { extra: this.props });
    }
  }

  render() {
    return (<ReconnectingWebSocket onMessage={this.handleAdminNotificationsMessage} path="/_ws/c/admin/notifications" />);
  }
}

PublicationLock.propTypes = {
  id: PropTypes.string.isRequired,
  history: PropTypes.shape({ push: PropTypes.func }),
  data: PropTypes.func,
  shouldUnlockOnUnmount: PropTypes.bool,
};

PublicationLock.defaultProps = {
  history: { push: () => {} },
  data: () => {},
  shouldUnlockOnUnmount: false,
};

export default withRouter(PublicationLock);
